import { z } from 'zod';
import { apiDate, cursorPagedResponseSchema, listResponseSchema } from '@/common/api/schemas.ts';
import { EventTypeEnum, eventTypeSchema } from '@/features/events/types/EventType.ts';
import { makeSchemaFieldsNullable } from '@/common/utils/makeSchemaFieldsNullable.ts';

export interface CreateDispenserData {
  sn: string;
  canisters: number;
  pos: string;
  typeId: string;
  collectorId: string | null;
  note: string;
}

export interface UpdateDispenserData extends CreateDispenserData {
  id: string;
}

export const dispenserDetailResponseSchema = z.object({
  id: z.string(),
  sn: z.string(),
  canisters: z.number(),
  pos: z.string(),
  type: z.object({
    id: z.string(),
    code: z.string(),
    producer: z.object({
      id: z.string(),
      code: z.string(),
    }),
  }),
  note: z.string(),
  lastMessageReceivedAt: apiDate((date) => date.nullable()),
  createdAt: apiDate(),
  collector: z
    .object({
      id: z.string(),
      computerId: z.string().nullable(),
      registrationInfo: z.json().nullable(),
      swVersion: z
        .object({
          name: z.string(),
          type: z.int32(),
          softwareVersion: z.string().nullable(),
          dbVersionList: z.array(
            z.object({
              path: z.string(),
              version: z.string(),
            })
          ),
        })
        .nullable(),
    })
    .nullable(),
});

export type DispenserDetailResponse = Omit<z.infer<typeof dispenserDetailResponseSchema>, 'pos'> & {
  pos: {
    id: number;
    name: string;
    country: string;
    street: string;
    streetNumber: string;
    city: string;
    zip: string;
  } | null;
};

export const dispenserListItemSchema = z.object({
  id: z.string(),
  sn: z.string(),
  canisters: z.number(),
  pos: z.string(),
  producer: z.string(),
  type: z.string(),
  computerId: z.string().nullable(),
  note: z.string(),
});

export const dispenserListResponseSchema = listResponseSchema(dispenserListItemSchema);

export type DispenserListItem = {
  id: string;
  sn: string;
  canisters: number;
  pos: string | null;
  country: string | null;
  producer: string | null;
  type: string | null;
  computerId: string | null;
};

export const eventSchema = z.object({
  eventAt: apiDate(),
  eventType: eventTypeSchema,
  eventId: z.string(),
  result: z.int(),
});

export type DispenserEvent = z.infer<typeof eventSchema>;

export const lastEventsResponseSchema = z.array(eventSchema);

export type LastEventsResponse = z.infer<typeof lastEventsResponseSchema>;

export interface ValidateSnData {
  sn: string;
}

export const validateSnResponseSchema = z.object({
  isUnique: z.boolean(),
});

export type ValidateSnResponse = z.infer<typeof validateSnResponseSchema>;

export const recirculationMetadataSchema = z.object({
  colorant: z.string(),
  channel: z.int32(),
});

export const recirculationPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.RECIRCULATION),
  ...makeSchemaFieldsNullable(recirculationMetadataSchema).partial().shape,
});

export const cleaningMetadataSchema = z.object({
  colorant: z.string(),
  channel: z.int32(),
  duration: z.number(),
});

export const cleaningPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.CLEANING),
  ...makeSchemaFieldsNullable(cleaningMetadataSchema).partial().shape,
});

export const dispensingMetadataSchema = z.object({
  colorRgb: z.tuple([z.int32(), z.int32(), z.int32()]).readonly(),
  product: z.string(),
  base: z.string(),
  fan: z.string(),
  package: z.string(),
  recipe: z.string(),
  colorants: z.array(
    z.object({
      name: z.string().nullable(),
    })
  ),
});

export const dispensingPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.DISPENSING),
  ...makeSchemaFieldsNullable(dispensingMetadataSchema).partial().shape,
});

export const errorMetadataSchema = z.object({
  canister: z.int32(),
  channel: z.int32(),
});

export const errorPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.ERROR),
  ...makeSchemaFieldsNullable(errorMetadataSchema).partial().shape,
});

export const purgeMetadataSchema = z.object({
  colorant: z.string(),
  amount: z.number(),
});

export const purgePayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.PURGE),
  ...makeSchemaFieldsNullable(purgeMetadataSchema).partial().shape,
});

export const flushMetadataSchema = z.object({
  colorant: z.string(),
  amount: z.number(),
});

export const flushPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.FLUSH),
  ...makeSchemaFieldsNullable(flushMetadataSchema).partial().shape,
});

export const refillMetadataSchema = z.object({
  colorant: z.string(),
  amount: z.int32(),
  canister: z.int32(),
  finalLevel: z.int32(),
});

export const refillPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.REFILL),
  ...makeSchemaFieldsNullable(refillMetadataSchema).partial().shape,
});

export const stirringMetadataSchema = z.object({
  colorant: z.string(),
  duration: z.number(),
  canister: z.int32(),
  intensity: z.number(),
});

export const stirringPayloadSchema = z.object({
  payloadType: z.literal(EventTypeEnum.STIRRING),
  ...makeSchemaFieldsNullable(stirringMetadataSchema).partial().shape,
});

const dispenserTimelineItemSchema = z.object({
  id: z.string().nullable(),
  startTime: apiDate(),
  startTimeUtcOffset: z.number(),
  endTime: apiDate(),
  endTimeUtcOffset: z.number(),
  eventType: eventTypeSchema,
  result: z.int().nullable(),
  resultsErrorCount: z.int(),
  eventsCount: z.int(),
  payload: z
    .discriminatedUnion('payloadType', [
      recirculationPayloadSchema,
      cleaningPayloadSchema,
      dispensingPayloadSchema,
      errorPayloadSchema,
      purgePayloadSchema,
      flushPayloadSchema,
      refillPayloadSchema,
      stirringPayloadSchema,
    ])
    .nullable(),
});

export type DispenserTimelineItem = z.infer<typeof dispenserTimelineItemSchema>;

export const dispenserTimelineResponseSchema = cursorPagedResponseSchema(
  dispenserTimelineItemSchema
);

export type DispenserTimelineResponse = z.infer<typeof dispenserTimelineResponseSchema>;

const dispenserTimelineDetailItemSchema = z.object({
  id: z.string(),
  eventAt: apiDate(),
  utcOffset: z.number(),
  eventType: eventTypeSchema,
  result: z.int(),
  payload: z
    .discriminatedUnion('payloadType', [
      recirculationPayloadSchema,
      cleaningPayloadSchema,
      dispensingPayloadSchema,
      errorPayloadSchema,
      purgePayloadSchema,
      flushPayloadSchema,
      refillPayloadSchema,
      stirringPayloadSchema,
    ])
    .nullable(),
});

export type DispenserTimelineDetailItem = z.infer<typeof dispenserTimelineDetailItemSchema>;

export const dispenserTimelineDetailResponseSchema = cursorPagedResponseSchema(
  dispenserTimelineDetailItemSchema
);

export type DispenserTimelineDetailResponse = z.infer<typeof dispenserTimelineDetailResponseSchema>;

export interface TimelineDetailRequestParams {
  startTime: Date;
  endTime: Date;
  eventType?: number;
  limit: number;
  cursor?: string;
}
